import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { SkyDropdown } from "@skypointcloud/skypoint-ui";
import { IRootReducerState } from "../../../../store/IRootReducer";
import { setPortalView, type PortalView } from "../../../../store/slices/uiSlice";
import { useGetMyPermissionsQuery } from "../../../../store/api/rbacApi";
import { ROUTES } from "../../../../components/shared/constant";
import { useMsalAuth } from "../../../../hooks/useMsalAuth";
import { getCustomDomainUrl } from "../../../../hooks/useCustomDomain";
import { useGetCustomDomainQuery } from "../../../../store/api/customDomainApi";
import {
  adminViewPath,
  isEmployeePath,
  isEmployeeUserViewPath,
  toUserViewPath,
} from "../../../../routes/employeePaths";
import { useEmployeeGate } from "../../../../hooks/useEmployeeGate";
import { employeeErrorCode, useLazyGetEmployeeMeQuery } from "../../../../store/api/employeeApi";
import { EMP_ERROR_CODES } from "../../../../../shared/employee/access";

// Temporarily disabled — waiting on wildcard DNS/TLS infra setup.
// Flip to true to restore custom-domain redirects from the portal switcher.
const CUSTOM_DOMAIN_UI_ENABLED = false;

const options = [
  { value: "admin" as PortalView, text: "Admin" },
  { value: "user" as PortalView, text: "User Portal" },
];

interface PortalViewDropdownProps {
  onViewChange?: () => void;
}

export function PortalViewDropdown({ onViewChange }: PortalViewDropdownProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { getQueryString } = useMsalAuth();
  const portalView = useSelector((state: IRootReducerState) => state.uiReducer.portalView);
  const { data: rbacPermissions } = useGetMyPermissionsQuery();
  const employeeGate = useEmployeeGate();
  const [checkEmployeeMe] = useLazyGetEmployeeMeQuery();

  // Use RBAC as single source of truth — stale Redux auth state caused user-only
  // roles to see the admin/user portal switch.
  const canSwitchPortal =
    rbacPermissions?.isAdmin === true ||
    rbacPermissions?.isB2CAdmin === true ||
    rbacPermissions?.isManager === true;

  const { data: domainData } = useGetCustomDomainQuery(undefined, {
    skip: !canSwitchPortal || !CUSTOM_DOMAIN_UI_ENABLED,
  });
  const customDomainUrl = CUSTOM_DOMAIN_UI_ENABLED
    ? getCustomDomainUrl(domainData?.prefix ?? null)
    : null;

  if (!canSwitchPortal) return null;

  // Inside skyEmployee the switch moves between skyEmployee's own admin and
  // employee-facing views. That view lives in the URL (/employee/my/...), so
  // the shared portalView state and the Service Desk customer portal are left
  // alone.
  const inEmployee = isEmployeePath(pathname);
  const currentView: PortalView = inEmployee
    ? isEmployeeUserViewPath(pathname)
      ? "user"
      : "admin"
    : portalView || "admin";

  const handleSelect = async (values: string[]) => {
    const value = values[0] as PortalView;
    if (value === currentView) return;

    onViewChange?.();
    const qs = getQueryString();

    if (inEmployee) {
      // Stay on the same page when it exists in both views; Home otherwise.
      if (value === "user") {
        navigate((toUserViewPath(pathname) ?? ROUTES.EMPLOYEE_MY_HOME) + qs);
      } else {
        const adminPage = adminViewPath(pathname);
        navigate((isEmployeeUserViewPath(adminPage) ? ROUTES.EMPLOYEE_HOME : adminPage) + qs);
      }
      return;
    }

    if (value === "user") {
      // With skyEmployee on for the instance, its employee-facing view is the
      // user portal from every product (Service Desk and WLM too); requests
      // are raised there. Otherwise the Service Desk customer portal, as before.
      // Someone skyEmployee says isn't set up there (a Manager with no
      // person record) keeps the Service Desk customer portal instead of a
      // "not set up" page. A failure to answer still opens skyEmployee,
      // which shows its own retry.
      if (employeeGate.enabledForInstance) {
        const me = await checkEmployeeMe(undefined, true);
        const code = employeeErrorCode(me.error);
        if (code !== EMP_ERROR_CODES.NOT_AN_EMPLOYEE && code !== EMP_ERROR_CODES.DISABLED) {
          navigate(ROUTES.EMPLOYEE_MY_HOME + qs);
          return;
        }
      }
      if (customDomainUrl) {
        window.location.href = customDomainUrl;
        return;
      }
      // Do NOT dispatch setPortalView here — it would flip isUserPortal before
      // the URL changes, letting AgentItemRedirect in PortalRoutes match the
      // old admin path and redirect to the customer ticket page instead of home.
      // The useEffect in AppLayout already syncs portalView to "user" once the
      // URL starts with /customer/.
      navigate(ROUTES.CUSTOMER_PORTALS + qs, { replace: true });
    } else {
      dispatch(setPortalView(value));
      navigate(ROUTES.HOME + qs, { replace: true });
    }
  };

  return (
    <div className="px-4 py-3 border-b border-gray-200">
      <SkyDropdown
        label="Portal View"
        size="small"
        options={options}
        selectedOptions={[currentView]}
        onOptionSelect={(values) => void handleSelect(values)}
      />
    </div>
  );
}
